import React, {
    createContext,
    useContext,
    useEffect,
    useState,
    useRef,
} from 'react';
import type { Company } from '../../services/rtkApi';
import { useCompanySearch } from './useCompanySearch';
import { MANUAL_ORG_NUMBER } from '.';

type CompanyContextType = {
    selectedCompany: Company | null;
    setSelectedCompany: (company: Company | null) => void;
    setCompanyClear: () => void;
};

const CompanyContext = createContext<CompanyContextType | undefined>(undefined);

export const useCompanyContext = () => {
    const context = useContext(CompanyContext);
    if (!context) {
        throw new Error(
            'useCompanyContext must be used within a CompanyProvider',
        );
    }
    return context;
};

export const CompanyProvider = ({
    children,
    syncWithForm,
    defaultValue,
}: {
    children: React.ReactNode;
    syncWithForm?: (company: Company | null) => void;
    defaultValue?: string;
}) => {
    const [selectedCompany, setSelectedCompany] = useState<Company | null>(
        null,
    );
    const prevOrgNumber = useRef<string | null>(defaultValue || null);
    const prevManualName = useRef<string | null>(null);
    const { getCompany, companyOptions } = useCompanySearch();

    useEffect(() => {
        if (!syncWithForm) return;
        if (!selectedCompany) return;

        const currentOrgNumber = selectedCompany.organisasjonsnummer;

        // Manual entries: deduplicate by name since org number stays "manuell"
        if (currentOrgNumber === MANUAL_ORG_NUMBER) {
            if (selectedCompany.navn !== prevManualName.current) {
                prevManualName.current = selectedCompany.navn;
                syncWithForm(selectedCompany);
            }
            return;
        }

        // Reset manual name ref when switching back to registry mode
        prevManualName.current = null;

        if (currentOrgNumber !== prevOrgNumber.current) {
            prevOrgNumber.current = currentOrgNumber;
            syncWithForm(selectedCompany);
        }
    }, [selectedCompany, syncWithForm]);

    // Fetch company details on load if defaultValue is provided
    useEffect(() => {
        // Guard to get company only if defaultValue is provided or value is changed
        // Skip API fetch for manually registered companies
        if (
            !defaultValue ||
            defaultValue === MANUAL_ORG_NUMBER ||
            selectedCompany?.organisasjonsnummer === defaultValue
        ) {
            return;
        }

        getCompany(defaultValue);
    }, [defaultValue, getCompany, selectedCompany]);

    // Update selectedCompany when API returns updated data
    useEffect(() => {
        if (!defaultValue) return;

        const foundCompany = companyOptions.find(
            (c) => c.organisasjonsnummer === defaultValue,
        );

        if (foundCompany) {
            setSelectedCompany(foundCompany);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [companyOptions]);

    const setCompanyClear = () => {
        setSelectedCompany(null);
        syncWithForm?.(null);
    };

    return (
        <CompanyContext.Provider
            value={{ selectedCompany, setSelectedCompany, setCompanyClear }}
        >
            {children}
        </CompanyContext.Provider>
    );
};
